import {
  hydrateCompactMenu,
  type ServiceType,
  type StorefrontAddonGroup,
  type StorefrontBranch,
  type StorefrontMenu,
  type StorefrontMenuItem,
  type StorefrontBootstrap,
  type StorefrontProductItem,
} from "@emynor/storefront-sdk";
import branchFallback from "@/assets/branch-interior.jpg";
import dishFallback from "@/assets/dish-grill.jpg";
import heroFallback from "@/assets/hero-feast.jpg";
import type {
  AdditionGroup,
  AppConfig,
  Branch,
  Category,
  DeliveryArea,
  Localized,
  MenuItem,
  Paginated,
} from "./types";
import { storefrontSdk } from "./sdk";
import { openAtScheduledTime } from "./branch-ordering";

const localized = (value: Record<string, string> | undefined): Localized => ({
  en: value?.["en"] ?? value?.["ar"] ?? Object.values(value ?? {})[0] ?? "",
  ar: value?.["ar"] ?? value?.["en"] ?? Object.values(value ?? {})[0] ?? "",
});

export function appConfigFromBootstrap(
  bootstrap: StorefrontBootstrap,
): AppConfig {
  const configuration = bootstrap.storefront?.configuration ?? {};
  const assetUrls = bootstrap.storefront?.assetUrls ?? {};
  const assetImages = bootstrap.storefront?.assetImages ?? {};
  const assets = (key: string) => {
    const ids = configuration[key];
    return Array.isArray(ids)
      ? ids.flatMap((id) => {
          if (typeof id !== "string") return [];
          const source = assetImages[id] ?? assetUrls[id];
          return source ? [source] : [];
        })
      : [];
  };
  const asset = (key: string) => {
    const id = configuration[key];
    return typeof id === "string"
      ? (assetImages[id] ?? assetUrls[id] ?? null)
      : null;
  };
  const configuredHomeBanners = assets("heroImageAssetIds");
  const cedarTableLocalPreview =
    import.meta.env.DEV &&
    bootstrap.mode !== "unpublished" &&
    bootstrap.tenant.slug === "cedar-table-local";
  const cedarTableBannerExamples = cedarTableLocalPreview
    ? localCedarTableBannerExamples()
    : null;
  const homeHeroBanners = cedarTableLocalPreview
    ? cedarTableBannerExamples!.home
    : configuredHomeBanners.length
      ? configuredHomeBanners
      : [heroFallback];
  return {
    title: localized(bootstrap.tenant.name),
    logo: bootstrap.tenant.logoImage ?? bootstrap.tenant.logoUrl ?? "",
    homeHeroBanners,
    dineInMenuImage: asset("dineInMenuImageAssetId"),
    onlineMenuImage: asset("onlineMenuImageAssetId"),
    heroTitle: localizedObject(configuration["heroTitle"]),
    heroDescription: localizedObject(configuration["heroDescription"]),
  };
}

function localCedarTableBannerExamples() {
  const photo = (id: string, height: number) =>
    `https://images.unsplash.com/${id}?auto=format&fit=crop&w=1920&h=${height}&q=82`;
  return {
    home: [
      photo("photo-1504674900247-0877df9cc836", 1080),
      photo("photo-1555939594-58d7cb561ad1", 1080),
      photo("photo-1540189549336-e6e99c3679fe", 1080),
    ],
  };
}

export async function getBranches(signal?: AbortSignal): Promise<Branch[]> {
  return (await storefrontSdk.branches(signal ? { signal } : undefined)).map(
    branchFromContract,
  );
}

export function categoriesFromMenu(menu: StorefrontMenu): Category[] {
  return menu.categories.map((category, index) => ({
    id: category.id,
    name: localized(category.name_i18n),
    description: localized(category.description_i18n),
    image: category.image_url ?? dishFallback,
    ...(category.image ? { responsiveImage: category.image } : {}),
    orderNumber: index,
  }));
}

export interface MenuItemQuery {
  branchId?: string;
  categoryId?: string;
  orderType: ServiceType;
  name?: string;
  page?: number;
  limit?: number;
}

export function menuItemsFromMenu(
  menu: StorefrontMenu,
  query: MenuItemQuery,
): Paginated<MenuItem> {
  if (!query.branchId)
    return { data: [], page: 1, limit: query.limit ?? 10, total: 0 };
  const term = query.name?.trim().toLowerCase() ?? "";
  const filtered = menu.items.filter((item) => {
    if (query.categoryId && item.category_id !== query.categoryId) return false;
    if (!term) return true;
    return `${Object.values(item.name_i18n).join(" ")} ${Object.values(item.description_i18n).join(" ")}`
      .toLowerCase()
      .includes(term);
  });
  const page = query.page ?? 1;
  const limit = query.limit ?? 10;
  return {
    data: filtered
      .slice((page - 1) * limit, page * limit)
      .map((item) => menuItemFromContract(item, query.orderType)),
    page,
    limit,
    total: filtered.length,
  };
}

export async function getMenuItemBySlug(
  slug: string,
  branchId?: string,
  orderType?: "delivery" | "pickup" | "indoor",
  signal?: AbortSignal,
): Promise<MenuItem | null> {
  const product = await storefrontSdk.product(
    slug,
    branchId && orderType ? { branchId, orderType } : undefined,
    signal ? { signal } : undefined,
  );
  return menuItemFromContract(
    product.item,
    orderType ?? "pickup",
    product.orderability,
    product.nextSaleChangeAt,
  );
}

export async function getDeliveryAreas(
  signal?: AbortSignal,
): Promise<DeliveryArea[]> {
  return (
    await storefrontSdk.deliveryZones(signal ? { signal } : undefined)
  ).map((zone) => {
    const center = { lat: zone.center_lat, lng: zone.center_lng };
    return {
      id: zone.id,
      branchId: zone.branch_ids[0] ?? "",
      name: { en: zone.name, ar: zone.name },
      available: zone.branch_ids.length > 0,
      fee: zone.delivery_fee_minor,
      minimumOrder: zone.minimum_order_minor,
      center,
      boundary: zone.boundary,
    };
  });
}

export type DeliveryCheckResult = {
  deliverable: boolean;
  reason?: string;
  branchId?: string;
  fee: number;
  minimumOrder: number;
  distanceKm: number;
};

export async function checkDeliveryAvailability(
  coords: {
    lat: number;
    lng: number;
  },
  signal?: AbortSignal,
): Promise<DeliveryCheckResult> {
  const result = await storefrontSdk.deliveryCheck(
    coords,
    signal ? { signal } : undefined,
  );
  return result.deliverable
    ? {
        deliverable: true,
        branchId: result.branchId,
        fee: result.fee.amount,
        minimumOrder: result.minimumOrder.amount,
        distanceKm: 0,
      }
    : {
        deliverable: false,
        reason: result.reason,
        fee: 0,
        minimumOrder: 0,
        distanceKm: 0,
      };
}

export async function getCompactMenu(
  branchId: string,
  orderType: ServiceType,
  signal?: AbortSignal,
): Promise<StorefrontMenu> {
  return hydrateCompactMenu(
    await storefrontSdk.compactMenu(
      branchId,
      orderType,
      signal ? { signal } : undefined,
    ),
  );
}

function branchFromContract(branch: StorefrontBranch): Branch {
  const dayName = new Intl.DateTimeFormat("en-US", { timeZone: branch.timezone, weekday: "short" }).format(new Date());
  const dayOfWeek = ["Sat", "Sun", "Mon", "Tue", "Wed", "Thu", "Fri"].indexOf(dayName);
  const today = branch.hours.find(
    (period) => period.day_of_week === dayOfWeek,
  );
  const weeklyHours = branch.hours.map((period) => ({
    dayOfWeek: period.day_of_week,
    opensAt: period.opens_at,
    closesAt: period.closes_at,
    closed: period.closed,
  }));
  return {
    id: branch.id,
    timezone: branch.timezone,
    openNow: branch.open_now ?? openAtScheduledTime({ timezone: branch.timezone, weeklyHours }, new Date()),
    nextOpening: branch.next_opening
      ? { dayOfWeek: branch.next_opening.day_of_week, opensAt: branch.next_opening.opens_at }
      : null,
    orderPause: { active: branch.order_pause?.active ?? false, resumesAt: branch.order_pause?.resumes_at ?? null },
    name: localized(branch.name_i18n),
    address: localized(branch.address_i18n),
    phones: branch.phone_numbers,
    image: branch.image_url ?? branchFallback,
    hasPhoto: Boolean(branch.image_url || branch.image),
    ...(branch.image ? { responsiveImage: branch.image } : {}),
    openingHours: {
      open: today?.opens_at ?? "00:00",
      close: today?.closes_at ?? "00:00",
      closed: today?.closed ?? true,
    },
    weeklyHours,
    translatedLocales: (["ar", "en"] as const).filter((locale) =>
      Boolean(branch.name_i18n[locale]?.trim()),
    ),
    coordinates:
      branch.lat !== null && branch.lng !== null
        ? { lat: branch.lat, lng: branch.lng }
        : null,
    ...(branch.youtube_url ? { youtubeUrl: branch.youtube_url } : {}),
    available: branch.enabled ?? true,
    indoorService: branch.has_indoor,
  };
}

function menuItemFromContract(
  item: StorefrontMenuItem | StorefrontProductItem,
  orderType: ServiceType,
  orderability?: {
    canAddToCart: boolean;
    reason:
      | import("@emynor/storefront-sdk").StorefrontProductOrderabilityReason
      | null;
  },
  nextSaleChangeAt?: string | null,
): MenuItem {
  const groups = addonGroups(item.addon_groups);
  const variations = item.variants
    .filter((variant) => variant.available)
    .map((variant) => {
      const variationGroups = addonGroups(variant.addon_groups);
      return {
        id: variant.id,
        name: localized(variant.name),
        description: localized(item.description_i18n),
        ...(variant.image_url ? { image: variant.image_url } : {}),
        ...(variant.image ? { responsiveImage: variant.image } : {}),
        price: variant.priceMinor ?? 0,
        originalPrice: variant.originalPriceMinor ?? null,
        branchPrice: variant.priceMinor ?? 0,
        additionGroups: variationGroups,
        additions: variationGroups.flatMap((group) => group.additions),
      };
    });
  const canSelect = item.item_type === "simple" || variations.length > 0;
  return {
    id: item.id,
    slug: item.slug,
    categoryId: item.category_id,
    name: localized(item.name_i18n),
    description: localized(item.description_i18n),
    image: item.image_url ?? "",
    ...(item.image ? { responsiveImage: item.image } : {}),
    type: item.item_type,
    price: item.price_minor ?? 0,
    originalPrice: item.original_price_minor ?? null,
    nextSaleChangeAt: nextSaleChangeAt ?? null,
    branchPrice: item.price_minor ?? 0,
    additionGroups: groups,
    additions: groups.flatMap((group) => group.additions),
    variations,
    indoorAvailable: orderType === "indoor" && canSelect,
    onlineAvailable:
      orderability?.canAddToCart ?? (orderType !== "indoor" && canSelect),
    featured: item.featured,
    ...(orderability?.reason
      ? { orderabilityReason: orderability.reason }
      : {}),
  };
}

function addonGroups(groups: StorefrontAddonGroup[]): AdditionGroup[] {
  return groups.map((group) => ({
    id: group.id,
    name: localized(group.name),
    minimum: group.minimum,
    maximum: group.maximum,
    additions: group.options
      .filter((option) => option.available)
      .map((option) => ({
        id: option.id,
        groupId: group.id,
        name: localized(option.name),
        price: option.priceMinor,
      })),
  }));
}

function localizedObject(value: unknown): Localized {
  return localized(
    value && typeof value === "object" && !Array.isArray(value)
      ? (value as Record<string, string>)
      : undefined,
  );
}
